import * as React from 'react';

// Components
import ConnectWithButton from './ConnectWithButton';
import IconConnectWithShopee from '@shopee-user/icons/IconConnectWithShopee';

// Hooks
import { useConnectWithShopee } from '../../../auth-provider/connect-with-shopee/hook';
import { useThirdPartyLoginFlowEvent } from '../flow-event';
import { useAutoRestoreState } from '@tob/shared/utils/react-helper';

// Constants & Enums
import { AuthenticateChannel } from '../../../core/types';
import { ShopeeAuthProvider } from '../../../auth-provider/connect-with-shopee/constants';
import { ErrorCode } from '@shopee-user/api-constants';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';

// Types
import type { ConnectWithButtonProps } from './ConnectWithButton';

import styles from './style.scss';
import { WithUserWebViewProps } from 'legacy-packages/with-web-view/src';

type Props = Omit<ConnectWithButtonProps, 'channel'> & {
  callbackUrl?: string;
  onAuthenticated?: ({
    authCode,
    appClientId,
  }: {
    authCode: string;
    appClientId?: string;
  }) => void | Promise<void>;
  onCancel?: ({
    appClientId,
  }: {
    appClientId?: string;
  }) => void | Promise<void>;
  onError?: ({
    appClientId,
    error,
    errorMsg,
  }: {
    appClientId?: string;
    error: number;
    errorMsg?: string;
  }) => void | Promise<void>;
  webViewGetAuthConfig: WithUserWebViewProps['webViewGetAuthConfig'];
  entryPoint: ThirdpartyBindingEntryPoint;
};

export default function ConnectWithShopee({
  className,
  label,
  layout,
  callbackUrl,
  onAuthenticated,
  onCancel,
  onError,
  webViewGetAuthConfig,
  entryPoint,
}: Props) {
  const [isDisabled, setIsDisabled] = useAutoRestoreState(false, {
    timeout: 3000,
  });
  const { authenticate } = useConnectWithShopee({
    webViewGetAuthConfig,
    entryPoint,
  });
  const isInited = React.useRef(false);

  const { trackEvent: trackLoginEvent } = useThirdPartyLoginFlowEvent();

  const startAuthentication = React.useCallback(async () => {
    trackLoginEvent('onThirdPartyLoginClick', {
      channel: AuthenticateChannel.Shopee,
    });

    setIsDisabled(true);
    const {
      authProvider,
      authCode,
      appClientId,
      isCancel,
      error,
      errorMsg,
    } = await authenticate();

    if (isCancel) {
      onCancel?.({ appClientId });
      setIsDisabled(false);
      return;
    }

    // we will do nothing in current page, because user will be redirected to Shopee web
    if (authProvider === ShopeeAuthProvider.ShopeeApp) {
      if (authCode) {
        await onAuthenticated?.({ authCode, appClientId });
      } else {
        onError?.({
          appClientId,
          error: error || ErrorCode.FeErrorCode,
          errorMsg,
        });
      }
      setIsDisabled(false);
    }
  }, [authenticate, callbackUrl, trackLoginEvent]);

  React.useEffect(() => {
    if (isInited.current) return;
    isInited.current = true;
    trackLoginEvent('onThirdPartyLoginComponentView', {
      channel: AuthenticateChannel.Shopee,
    });
  }, []);

  return (
    <ConnectWithButton
      channel={AuthenticateChannel.Shopee}
      icon={<IconConnectWithShopee className={styles.icon} />}
      className={className}
      disabled={isDisabled}
      label={label}
      layout={layout}
      onClick={startAuthentication}
    />
  );
}
